/**
 * Service for handling date storage operations
 */

import type { CampaignDates, StoredDateData } from '../types/DurationTypes';

export class DateStorageService {
  private static readonly STORAGE_KEYS = {
    START_DATE: 'startDate',
    END_DATE: 'endDate',
    DURATION: 'duration',
    FORM_DATA: 'formData',
    SELECTION_MODE: 'dateSelectionMode',
    SELECTED_DATES: 'selectedDates'
  } as const;

  /**
   * Load saved dates from localStorage
   */
  static loadSavedDates(): StoredDateData {
    return {
      startDate: localStorage.getItem(this.STORAGE_KEYS.START_DATE),
      endDate: localStorage.getItem(this.STORAGE_KEYS.END_DATE),
      duration: localStorage.getItem(this.STORAGE_KEYS.DURATION)
    };
  }

  /**
   * Save a date to localStorage with proper formatting
   * @param date - The date to save
   * @param dateType - Whether this is a start or end date
   * @param options - Optional settings (silent: don't dispatch event to avoid cross-hook interference)
   */
  static saveDate(date: Date, dateType: 'start' | 'end', options?: { silent?: boolean }): void {
    const storageKey = dateType === 'start' 
      ? this.STORAGE_KEYS.START_DATE 
      : this.STORAGE_KEYS.END_DATE;
    
    const formDataKey = dateType === 'start' ? 'startDate' : 'endDate';
    
    // Format date as YYYY-MM-DD
    const formattedDate = this.formatDateForStorage(date);
    
    // Store both ISO string and formatted date
    localStorage.setItem(storageKey, date.toISOString());
    localStorage.setItem(`${storageKey}_exact`, formattedDate);
    
    // Update form data
    this.updateFormData(formDataKey, formattedDate);
    
    console.log(`Setting ${storageKey} in localStorage:`, {
      isoString: date.toISOString(),
      exactYYYYMMDD: formattedDate
    });
    
    // Dispatch custom event for same-tab listeners (unless silent mode)
    if (!options?.silent) {
      window.dispatchEvent(new CustomEvent('campaignDatesChanged', { 
        detail: { dateType, date: date.toISOString() } 
      }));
    }
  }

  /**
   * Save duration to localStorage
   */
  static saveDuration(days: number): void {
    localStorage.setItem(this.STORAGE_KEYS.DURATION, days.toString());
    this.updateFormData('duration', days);
    console.log(`Duration set to ${days} days`);
  }

  /**
   * Clear end date from storage
   */
  static clearEndDate(): void {
    localStorage.removeItem(this.STORAGE_KEYS.END_DATE);
    localStorage.removeItem(`${this.STORAGE_KEYS.END_DATE}_exact`);
    
    const formData = this.getFormData();
    delete formData.endDate;
    this.saveFormData(formData);
  }

  /**
   * Parse stored date with validation
   */
  static parseStoredDate(dateString: string | null, minDate: Date): Date | null {
    if (!dateString || dateString.trim() === '') return null;

    try {
      const parsedDate = new Date(dateString);
      
      // Validate date is not NaN and is a valid date
      if (!isNaN(parsedDate.getTime())) {
        console.log(`✅ Successfully parsed stored date: ${parsedDate.toISOString()}`);
        return parsedDate;
      } else {
        console.warn(`⚠️ Stored date ${dateString} could not be parsed`);
        return null;
      }
    } catch (error) {
      console.error("Error parsing stored date:", error);
      return null;
    }
  }

  /**
   * Format date as YYYY-MM-DD
   */
  private static formatDateForStorage(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Get form data from localStorage
   */
  private static getFormData(): Record<string, any> {
    try {
      return JSON.parse(localStorage.getItem(this.STORAGE_KEYS.FORM_DATA) || '{}');
    } catch {
      return {};
    }
  }

  /**
   * Save form data to localStorage
   */
  private static saveFormData(data: Record<string, any>): void {
    localStorage.setItem(this.STORAGE_KEYS.FORM_DATA, JSON.stringify(data));
  }

  /**
   * Update specific field in form data
   */
  private static updateFormData(key: string, value: any): void {
    const formData = this.getFormData();
    formData[key] = value;
    this.saveFormData(formData);
  }

  /**
   * Save date selection mode
   */
  static saveSelectionMode(mode: 'range' | 'specific'): void {
    localStorage.setItem(this.STORAGE_KEYS.SELECTION_MODE, mode);
  }

  /**
   * Load date selection mode
   */
  static loadSelectionMode(): 'range' | 'specific' {
    const stored = localStorage.getItem(this.STORAGE_KEYS.SELECTION_MODE);
    return stored === 'specific' ? 'specific' : 'range';
  }

  /**
   * Save multiple selected dates (for specific mode)
   */
  static saveMultiDates(dates: Date[]): void {
    const dateStrings = dates.map(d => d.toISOString());
    localStorage.setItem(this.STORAGE_KEYS.SELECTED_DATES, JSON.stringify(dateStrings));
    
    // Also update formData with the dates array
    const formData = this.getFormData();
    formData.selectedDates = dateStrings;
    formData.dateSelectionMode = 'specific';
    this.saveFormData(formData);
  }

  /**
   * Load multiple selected dates
   */
  static loadMultiDates(minDate: Date): Date[] {
    const stored = localStorage.getItem(this.STORAGE_KEYS.SELECTED_DATES);
    if (!stored) return [];
    
    try {
      const parsed = JSON.parse(stored);
      // Normalize minDate for comparison (remove time component)
      const minDateNormalized = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
      
      return parsed
        .map((dateStr: string) => new Date(dateStr))
        .filter((d: Date) => {
          if (isNaN(d.getTime())) return false;
          // Normalize stored date for comparison
          const dNormalized = new Date(d.getFullYear(), d.getMonth(), d.getDate());
          return dNormalized >= minDateNormalized;
        });
    } catch {
      return [];
    }
  }

  /**
   * Clear multi-date selection
   */
  static clearMultiDates(): void {
    localStorage.removeItem(this.STORAGE_KEYS.SELECTED_DATES);
    const formData = this.getFormData();
    delete formData.selectedDates;
    this.saveFormData(formData);
  }
}